import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { useAuthStore } from "../stores/auth";
import { useToast } from "../composables/useToast";
import { t } from "../i18n";
import { mustChangePasswordDecision } from "./guards";

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "login",
    component: () => import("../pages/LoginPage.vue"),
    meta: { bare: true, publicOnly: true },
  },
  {
    path: "/",
    name: "dashboard",
    component: () => import("../pages/DashboardPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Mindbox-style URL — Russian UI says «Клиенты». /profiles is kept as alias
    // for backward compat (DEMO.md docs, bookmarks from earlier sessions).
    path: "/clients",
    alias: ["/profiles"],
    name: "profiles",
    component: () => import("../pages/ProfilesPage.vue"),
    meta: { permission: "profiles:list", requiresAuth: true },
  },
  {
    path: "/clients/:id",
    alias: ["/profiles/:id"],
    name: "profile-detail",
    component: () => import("../pages/ProfileDetailPage.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: "/diagnostics",
    name: "diagnostics",
    component: () => import("../pages/DiagnosticsPage.vue"),
    meta: { permission: "diagnostics:simulate_send", requiresAuth: true },
  },
  {
    // D1 «AI-Decisioning» — политики решений (deny/allow по цели/каналу),
    // создание из обычного текста через ИИ.
    path: "/decision-policies",
    name: "decision-policies",
    component: () => import("../pages/DecisionPoliciesPage.vue"),
    meta: { requiresAuth: true, permission: "policies:read" },
  },
  {
    // D2 «AI-Decisioning / NBO» — каталог предложений (next-best-offer).
    // Подбор лучшего предложения под клиента виден в карточке клиента 360°.
    path: "/offers",
    name: "offers",
    component: () => import("../pages/OffersPage.vue"),
    meta: { permission: "offers:list", requiresAuth: true },
  },
  {
    // P0⑤ конструктора кампаний — матрица «предложение × канал → шаблон».
    // Самостоятельный раздел; компонент матрицы позже встанет вкладкой
    // редактора кампании.
    path: "/channel-templates",
    name: "channel-templates",
    component: () => import("../pages/ChannelTemplatesPage.vue"),
    meta: { permission: "templates:read", requiresAuth: true },
  },
  {
    // RTD Part C «Реактивный NBO» — событийные триггеры: при приходе указанного
    // события движок считает next-best-offer и показывает контекстно (веб-попап).
    path: "/realtime-nbo",
    name: "realtime-nbo",
    component: () => import("../pages/RealtimeNboTriggersPage.vue"),
    meta: { permission: "nbo_triggers:read", requiresAuth: true },
  },
  {
    // Wave-3 «Кампания решений» — оркестрация портфеля (аудитория × предложения
    // × каналы × бюджет → план распределения). Список кампаний.
    path: "/decision-campaigns",
    name: "decision-campaigns",
    component: () => import("../pages/DecisionCampaignsPage.vue"),
    meta: { permission: "campaigns:read", requiresAuth: true },
  },
  {
    // Создание новой кампании решений. Статический /new ПЕРЕД /:id, чтобы «new»
    // не парсился как id (используем тот же редактор-страницу, что и деталь).
    path: "/decision-campaigns/new",
    name: "decision-campaign-new",
    component: () => import("../pages/DecisionCampaignDetailPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Деталь/редактор кампании: настройки + DRY-RUN план + утверждение.
    path: "/decision-campaigns/:id",
    name: "decision-campaign-detail",
    component: () => import("../pages/DecisionCampaignDetailPage.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: "/segments",
    name: "segments",
    component: () => import("../pages/SegmentsPage.vue"),
    meta: { permission: "segments:read", requiresAuth: true },
  },
  {
    path: "/segments/overlap",
    name: "segments-overlap",
    component: () => import("../pages/SegmentOverlapPage.vue"),
    meta: { permission: "segments:read", requiresAuth: true },
  },
  {
    path: "/exports",
    name: "exports",
    component: () => import("../pages/ExportsPage.vue"),
    meta: { permission: "segments:export", requiresAuth: true },
  },
  {
    path: "/segments/new",
    name: "segments-new",
    component: () => import("../pages/SegmentEditPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Mindbox-style — Russian UI says «Сценарии». /journeys kept as alias.
    path: "/scenarios",
    alias: ["/journeys"],
    name: "journeys",
    component: () => import("../pages/JourneysPage.vue"),
    meta: { permission: "journeys:list", requiresAuth: true },
  },
  {
    path: "/scenarios/new",
    alias: ["/journeys/new"],
    name: "journey-new",
    component: () => import("../pages/JourneyEditPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Generic /scenarios/:id detail. Registered AFTER /scenarios/new so router
    // matches the static path first.
    path: "/scenarios/:id",
    alias: ["/journeys/:id"],
    name: "journey-detail",
    component: () => import("../pages/JourneyDetailPage.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: "/scenarios/:id/edit",
    alias: ["/journeys/:id/edit"],
    name: "journey-edit",
    component: () => import("../pages/JourneyEditPage.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  // ── Batch Flow дизайнер — «Пакетная кампания» (НЕ путать с decision-campaigns
  // «Кампания решений»). Канонический порядок блока: список → /new (статический,
  // ПЕРЕД /:id) → /:id деталь → /:id/edit редактор.
  {
    // Список пакетных кампаний.
    path: "/campaigns",
    name: "campaigns",
    component: () => import("../pages/CampaignsPage.vue"),
    meta: { permission: "campaigns:read", requiresAuth: true },
  },
  {
    // Конструктор ботов — список.
    path: "/bots",
    name: "bots",
    component: () => import("../pages/BotsListPage.vue"),
    meta: { permission: "bots:list", requiresAuth: true },
  },
  {
    // Редактор бота: подключение канала + сценарии-графы.
    path: "/bots/:id",
    name: "bot-edit",
    component: () => import("../pages/BotEditPage.vue"),
    props: true,
    meta: { permission: "bots:read", requiresAuth: true },
  },
  {
    // Инбокс «Диалоги» — живой чат оператора с клиентами ботов.
    path: "/dialogs",
    name: "dialogs",
    component: () => import("../pages/DialogsInboxPage.vue"),
    meta: { permission: "dialogs:list", requiresAuth: true },
  },
  {
    // Редактор пакетной кампании (СОЗДАНИЕ). Статический /new ПЕРЕД /:id, иначе
    // «new» распарсится как id. Тот же компонент, что и /edit (isNew по
    // отсутствию route.params.id).
    path: "/campaigns/new",
    name: "campaign-new",
    component: () => import("../pages/CampaignEditPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Деталь/сведения кампании (граф, прогоны). Параметрический — ПОСЛЕ /new.
    path: "/campaigns/:id",
    name: "campaign-detail",
    component: () => import("../pages/CampaignDetailPage.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    // Редактор пакетной кампании (РЕДАКТИРОВАНИЕ существующей).
    path: "/campaigns/:id/edit",
    name: "campaign-edit",
    component: () => import("../pages/CampaignEditPage.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    // Интерстишл «Что создать?» по кнопке «Создать» (Рассылка / Пакетная
    // кампания / Событийный сценарий). В боковом меню НЕ показывается.
    path: "/create",
    name: "create-chooser",
    component: () => import("../pages/CreateChooserPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/templates",
    name: "templates",
    component: () => import("../pages/TemplatesPage.vue"),
    meta: { permission: "templates:read", requiresAuth: true },
  },
  {
    path: "/templates/new",
    name: "template-new",
    component: () => import("../pages/TemplateEditPage.vue"),
    props: { mode: "new" },
    meta: { requiresAuth: true },
  },
  {
    path: "/templates/:id/edit",
    name: "template-edit",
    component: () => import("../pages/TemplateEditPage.vue"),
    props: (route) => ({ mode: "edit", id: route.params.id as string }),
    meta: { requiresAuth: true },
  },
  {
    // Full-screen block email editor (GrapesJS + MJML), no AppShell sidebar.
    path: "/templates/:id/email-builder",
    name: "email-builder",
    component: () => import("../pages/EmailBuilderPage.vue"),
    props: (route) => ({ id: route.params.id as string }),
    meta: { requiresAuth: true, bare: true },
  },
  {
    // Email template gallery (ready-made starter designs).
    path: "/email-templates/library",
    name: "email-library",
    component: () => import("../pages/EmailTemplateLibraryPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Convenience: /templates/:id (без /edit) — отправляет в editor с
    // открытым preview tab. На демо удобно — кликнул в списке, увидел.
    path: "/templates/:id",
    redirect: (route) => `/templates/${route.params.id}/edit`,
  },
  {
    path: "/analytics",
    name: "analytics",
    component: () => import("../pages/AnalyticsPage.vue"),
    meta: { permission: "analytics:view", requiresAuth: true },
  },
  {
    path: "/analytics/lift",
    name: "analytics-lift",
    component: () => import("../pages/LiftDashboardPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/analytics/funnel",
    name: "analytics-funnel",
    component: () => import("../pages/AnalyticsFunnelPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/analytics/cohort",
    name: "analytics-cohort",
    component: () => import("../pages/AnalyticsCohortPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/analytics/deliverability",
    name: "analytics-deliverability",
    component: () => import("../pages/DeliverabilityPage.vue"),
    meta: { permission: "analytics:view", requiresAuth: true },
  },
  {
    path: "/analytics/sms",
    name: "analytics-sms",
    component: () => import("../pages/AnalyticsSmsPage.vue"),
    meta: { permission: "analytics:view", requiresAuth: true },
  },
  {
    path: "/analytics/push",
    name: "analytics-push",
    component: () => import("../pages/AnalyticsPushPage.vue"),
    meta: { permission: "analytics:view", requiresAuth: true },
  },
  {
    path: "/analytics/channels",
    name: "analytics-channels",
    component: () => import("../pages/AnalyticsChannelsPage.vue"),
    meta: { permission: "analytics:view", requiresAuth: true },
  },
  {
    path: "/analytics/email",
    name: "analytics-email",
    component: () => import("../pages/AnalyticsEmailPage.vue"),
    meta: { permission: "analytics:view", requiresAuth: true },
  },
  {
    path: "/analytics/messengers",
    name: "analytics-messengers",
    component: () => import("../pages/AnalyticsMessengersPage.vue"),
    meta: { permission: "analytics:view", requiresAuth: true },
  },
  {
    path: "/analytics/cross",
    name: "analytics-cross",
    component: () => import("../pages/AnalyticsCrossPage.vue"),
    meta: { permission: "analytics:view", requiresAuth: true },
  },
  {
    path: "/analytics/revenue",
    name: "analytics-revenue",
    component: () => import("../pages/AnalyticsRevenuePage.vue"),
    meta: { permission: "analytics:view", requiresAuth: true },
  },
  {
    path: "/analytics/gamification",
    name: "analytics-gamification",
    component: () => import("../pages/AnalyticsGamificationPage.vue"),
    meta: { permission: "analytics:view", requiresAuth: true },
  },
  {
    path: "/analytics/bots",
    name: "analytics-bots",
    component: () => import("../pages/AnalyticsBotsPage.vue"),
    meta: { permission: "analytics:view", requiresAuth: true },
  },
  {
    path: "/analytics/operators",
    name: "analytics-operators",
    component: () => import("../pages/AnalyticsOperatorsPage.vue"),
    meta: { permission: "analytics:view", requiresAuth: true },
  },
  {
    path: "/segments/rfm",
    name: "segments-rfm",
    component: () => import("../pages/SegmentsRFMPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Канон навигации (ADR-universal-navigation, задача 10): /segments/:id =
    // ДЕТАЛЬ (обзор), /segments/:id/edit = редактор. Регистрируем ПОСЛЕ
    // статических /new, /rfm, /overlap; /:id/edit перед /:id (более специфичный).
    path: "/segments/:id/edit",
    name: "segment-edit",
    component: () => import("../pages/SegmentEditPage.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: "/segments/:id",
    name: "segment-detail",
    component: () => import("../pages/SegmentDetailPage.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: "/preference-topics",
    name: "preference-topics",
    component: () => import("../pages/PreferenceTopicsPage.vue"),
    meta: { permission: "preferences:read", requiresAuth: true },
  },
  {
    path: "/attribute-dictionary",
    name: "attribute-dictionary",
    component: () => import("../pages/AttributeDictionaryPage.vue"),
    meta: { requiresAuth: true },
  },
  // B2B многосущностная модель (P1) — Юрлица / Владения / Связи. Список →
  // /:id карточка (идентификаторы / владения / связи). Доступ — `entities:*`.
  {
    path: "/entities",
    name: "entities",
    component: () => import("../pages/EntitiesPage.vue"),
    meta: { requiresAuth: true, permission: "entities:read" },
  },
  {
    path: "/entities/:id",
    name: "entity-detail",
    component: () => import("../pages/EntityDetailPage.vue"),
    props: true,
    meta: { requiresAuth: true, permission: "entities:read" },
  },
  {
    path: "/channels",
    name: "channels",
    component: () => import("../pages/ChannelsPage.vue"),
    meta: { permission: "channels:list", requiresAuth: true },
  },
  {
    path: "/channel-originators",
    name: "channel-originators",
    component: () => import("../pages/ChannelOriginatorsPage.vue"),
    meta: { permission: "channel_originators:read", requiresAuth: true },
  },
  // Wave 1 (Phase B+C) pages — added 2026-05-27 ночью.
  { path: "/loyalty/tiers",      name: "loyalty-tiers",    component: () => import("../pages/LoyaltyTiersPage.vue"),    meta: { requiresAuth: true } },
  { path: "/loyalty/rewards",    name: "loyalty-rewards",  component: () => import("../pages/RewardsCatalogPage.vue"),  meta: { requiresAuth: true } },
  { path: "/loyalty/currencies", name: "loyalty-currencies", component: () => import("../pages/CurrenciesPage.vue"),   meta: { requiresAuth: true } },
  { path: "/loyalty/cashback",   name: "loyalty-cashback", component: () => import("../pages/CashbackPolicyPage.vue"),  meta: { requiresAuth: true } },
  { path: "/loyalty/partners",   name: "loyalty-partners", component: () => import("../pages/LoyaltyPartnersPage.vue"), meta: { permission: "loyalty:read", requiresAuth: true } },
  { path: "/loyalty/promotions", name: "loyalty-promotions", component: () => import("../pages/PromotionsPage.vue"),   meta: { permission: "loyalty:read", requiresAuth: true } },
  { path: "/loyalty/bonuses",    name: "loyalty-bonuses",  component: () => import("../pages/LoyaltyBonusPolicyPage.vue"), meta: { requiresAuth: true } },
  { path: "/loyalty/card",       name: "loyalty-card",     component: () => import("../pages/LoyaltyCardPage.vue"),      meta: { requiresAuth: true } },
  { path: "/loyalty/card-templates", name: "loyalty-card-templates", component: () => import("../pages/LoyaltyCardTemplatesPage.vue"), meta: { requiresAuth: true } },
  { path: "/loyalty/overview", name: "loyalty-overview", component: () => import("../pages/LoyaltyOverviewPage.vue"), meta: { requiresAuth: true } },
  { path: "/loyalty/recipes", name: "loyalty-recipes", component: () => import("../pages/LoyaltyRecipesPage.vue"), meta: { requiresAuth: true } },
  { path: "/event-sandbox", name: "event-sandbox", component: () => import("../pages/EventSandboxPage.vue"), meta: { requiresAuth: true } },
  { path: "/loyalty/wallet-campaigns", name: "loyalty-wallet-campaigns", component: () => import("../pages/WalletCampaignsPage.vue"), meta: { requiresAuth: true } },
  { path: "/loyalty/analytics-cards", name: "loyalty-analytics-cards", component: () => import("../pages/AnalyticsLoyaltyCardsPage.vue"), meta: { requiresAuth: true } },
  { path: "/loyalty/security",   name: "loyalty-security", component: () => import("../pages/LoyaltySecurityPage.vue"), meta: { permission: "loyalty:read", requiresAuth: true } },
  { path: "/loyalty/reports",    name: "loyalty-reports",  component: () => import("../pages/AnalyticsLoyaltyReportsPage.vue"), meta: { permission: "analytics:view", requiresAuth: true } },
  { path: "/promo-codes",        name: "promo-codes",      component: () => import("../pages/PromoCodesPage.vue"),      meta: { requiresAuth: true } },
  { path: "/loyalty/promo-pools", name: "promo-pools",     component: () => import("../pages/PromoPoolsPage.vue"),      meta: { permission: "loyalty:read", requiresAuth: true } },
  { path: "/missions",           name: "missions",         component: () => import("../pages/MissionsPage.vue"),        meta: { requiresAuth: true } },
  { path: "/achievements",       name: "achievements",     component: () => import("../pages/AchievementsPage.vue"),    meta: { requiresAuth: true } },
  { path: "/experiments",        name: "experiments",      component: () => import("../pages/ExperimentsPage.vue"),     meta: { requiresAuth: true } },
  { path: "/external-segments",  name: "external-segments",component: () => import("../pages/ExternalSegmentsPage.vue"),meta: { requiresAuth: true } },
  // Фаза 4 — подключения к витринам данных (федеративные внешние сегменты).
  // Admin-only: бэкенд-гард по правам warehouse_connections:* — здесь meta.permission
  // держит страницу из UI у роли без права (как у других чувствительных разделов).
  { path: "/data-connections",   name: "data-connections", component: () => import("../pages/DataConnectionsPage.vue"), meta: { requiresAuth: true, permission: "warehouse_connections:list" } },
  // Фаза 4 — федеративные сегменты (аудитория считается на витрине данных, zero-copy).
  // Admin-only: бэкенд-гард по правам federated_segments:* — meta.permission держит
  // страницу из UI у роли без права чтения (как у других чувствительных разделов).
  // #9/§5 — список федеративных СВЁРНУТ в /segments (фасад с фильтром по типу). Голый
  // /federated-segments редиректит туда; единая ДЕТАЛЬ по типу — /federated-segments/:id
  // (механика федеративного: размер/пересчёт/снимок/редактирование + D1-плашка), создание —
  // /federated-segments/new.
  { path: "/federated-segments", redirect: "/segments" },
  { path: "/federated-segments/:id", name: "federated-segment-detail", component: () => import("../pages/FederatedSegmentsPage.vue"), meta: { requiresAuth: true, permission: "federated_segments:read" } },
  // #8/D2 — страница и сущность «федеративные композиции» УПРАЗДНЕНЫ как UI: их роль
  // выполняет «Собрать из сегментов» (compose) с федеративными членами; исполнитель
  // (stream-intersect) остаётся как ВНУТРЕННИЙ движок set-ops, не как UI-сущность.
  { path: "/reverse-etl",        name: "reverse-etl",      component: () => import("../pages/ReverseEtlPage.vue"),      meta: { requiresAuth: true, permission: "reverse_etl:read" } },
  { path: "/cost-estimate",      name: "cost-estimate",    component: () => import("../pages/CostEstimatePage.vue"),    meta: { requiresAuth: true } },
  { path: "/billing-showcase",   name: "billing-showcase", component: () => import("../pages/BillingShowcasePage.vue"), meta: { requiresAuth: true } },
  { path: "/ml-training",        name: "ml-training",      component: () => import("../pages/MlTrainingRunsPage.vue"),  meta: { requiresAuth: true } },
  // Wave 2 — added 2026-05-27 ночью.
  { path: "/broadcasts",          name: "broadcasts",       component: () => import("../pages/BroadcastsPage.vue"),      meta: { requiresAuth: true } },
  // Phase B — многошаговый мастер рассылки (заменяет модал создания). Static
  // `/broadcasts/new` must precede `/broadcasts/:id` so "new" is not an id.
  { path: "/broadcasts/new",      name: "broadcast-new",    component: () => import("../pages/BroadcastWizardPage.vue"), meta: { requiresAuth: true } },
  // Phase B remnant — edit now uses the SAME wizard (loads + prefills the broadcast).
  { path: "/broadcasts/:id/edit", name: "broadcast-edit",   component: () => import("../pages/BroadcastWizardPage.vue"), props: true, meta: { requiresAuth: true } },
  { path: "/broadcasts/:id",      name: "broadcast-detail", component: () => import("../pages/BroadcastDetailPage.vue"), props: true, meta: { requiresAuth: true } },
  { path: "/waiting-baskets",     name: "waiting-baskets",  component: () => import("../pages/WaitingBasketsPage.vue"),     meta: { requiresAuth: true } },
  { path: "/waiting-baskets/:id", name: "waiting-basket-detail", component: () => import("../pages/WaitingBasketDetailPage.vue"), props: true, meta: { requiresAuth: true } },
  // Phase C (C2) — visible contact policy (frequency caps + quiet hours).
  // P0④ конструктора кампаний — единый governance-хаб «Контактная политика»
  // (вкладки: частотные лимиты/тихие часы, контрольная группа, стоп-листы,
  // согласия). Старый ContactPolicyPage остаётся — встроен в хаб как вкладка.
  { path: "/contact-policy",      name: "contact-policy",   component: () => import("../pages/ContactPolicyHubPage.vue"),   meta: { requiresAuth: true } },
  { path: "/cascades",            name: "cascade-policies", component: () => import("../pages/CascadePoliciesPage.vue"),    meta: { requiresAuth: true } },
  { path: "/cascades/:id",        name: "cascade-policy-detail", component: () => import("../pages/CascadePolicyDetailPage.vue"), props: true, meta: { requiresAuth: true } },
  { path: "/approvals",           name: "approvals",        component: () => import("../pages/ApprovalsPage.vue"),          meta: { requiresAuth: true } },
  // Wave 2.1 — added 2026-05-27.
  { path: "/audit-log",           name: "audit-log",        component: () => import("../pages/AuditLogPage.vue"),           meta: { requiresAuth: true, permission: "audit_log:read" } },
  // 2026-05-29 — global event_log viewer (storefront / bot / journey).
  { path: "/event-log",           name: "event-log",        component: () => import("../pages/EventLogPage.vue"),           meta: { requiresAuth: true } },
  // Внешний pull-export потока событий партнёру-агрегатору (в его BI/CRM).
  { path: "/events-export",       name: "events-export",    component: () => import("../pages/EventsExportPage.vue"),       meta: { requiresAuth: true, permission: "events:read" } },
  // Realtime-пуш событий партнёру: подписки на вебхуки (#1 эпика агрегатора).
  { path: "/webhook-subscriptions", name: "webhook-subscriptions", component: () => import("../pages/WebhookSubscriptionsPage.vue"), meta: { requiresAuth: true, permission: "webhook_subscriptions:read" } },
  { path: "/webhook-subscriptions/:id", name: "webhook-subscription-detail", component: () => import("../pages/WebhookSubscriptionDetailPage.vue"), meta: { requiresAuth: true, permission: "webhook_subscriptions:read" } },
  { path: "/outbox",              name: "outbox",           component: () => import("../pages/OutboxPage.vue"),             meta: { requiresAuth: true } },
  { path: "/api-tokens",          name: "api-tokens",       component: () => import("../pages/ApiTokensPage.vue"),          meta: { requiresAuth: true, permission: "api_tokens:read" } },
  { path: "/write-keys",          name: "write-keys",       component: () => import("../pages/WriteKeysPage.vue"),          meta: { requiresAuth: true, permission: "write_keys:read" } },
  // Чат-центр CDP — настройки канала (по конкретному write-key со scope 'chat'),
  // достижима ТОЛЬКО действием со страницы write-keys, отдельного пункта меню нет.
  { path: "/write-keys/:id/chat-settings", name: "chat-channel-settings", component: () => import("../pages/ChatChannelSettingsPage.vue"), meta: { requiresAuth: true, permission: "chat:read" } },
  // Каталог контентных лент — CRUD content_feed_templates.
  { path: "/content-feeds",       name: "content-feeds",    component: () => import("../pages/ContentFeedsPage.vue"),       meta: { requiresAuth: true, permission: "chat:list" } },
  // Публикации внутри ОДНОЙ ленты — достижима переходом из каталога лент, не отдельный пункт меню.
  { path: "/content-feeds/:feedId/publications", name: "content-feed-publications", component: () => import("../pages/ContentFeedPublicationsPage.vue"), meta: { requiresAuth: true, permission: "chat:list" } },
  { path: "/tester",              name: "tester",           component: () => import("../pages/TesterPage.vue"),             meta: { requiresAuth: true } },
  // Квизабра Трек 2.1 — сегмент-триггеры (win-back без SQL).
  { path: "/segment-triggers",    name: "segment-triggers", component: () => import("../pages/SegmentTriggersPage.vue"),    meta: { requiresAuth: true } },
  { path: "/experiences",         name: "experiences",      component: () => import("../pages/ExperiencesPage.vue"),         meta: { requiresAuth: true } },
  // UX-унификация: создание/правка веб-инструмента — отдельная СТРАНИЦА-редактор
  // (ExperienceEditPage), а не модалка. Статический /new ПЕРЕД /:id, чтобы «new»
  // не парсился как id. /:id/edit — редактор существующей записи.
  { path: "/experiences/new",      name: "experience-new",     component: () => import("../pages/ExperienceEditPage.vue"),    meta: { requiresAuth: true } },
  { path: "/experiences/:id/edit", name: "experience-edit-id", component: () => import("../pages/ExperienceEditPage.vue"),   props: true, meta: { requiresAuth: true } },
  // C2 — per-experience incremental-lift «Эффект» page (reachable from the list
  // row «Эффект» button). /:id matches the detail/report after /new + /:id/edit.
  { path: "/experiences/:id",     name: "experience-detail", component: () => import("../pages/ExperienceDetailPage.vue"), props: true, meta: { requiresAuth: true } },
  // App-инструменты (Ф2 редизайна чата) — тот же движок Experience, но surface=mobile.
  // Раздел меню параллельный «Веб-инструментам»; переиспользует ExperiencesPage/ExperienceEditPage,
  // которые определяют surface по префиксу пути (/app-tools → mobile). Первый тип — «Мобильный чат».
  { path: "/app-tools",           name: "app-tools",          component: () => import("../pages/ExperiencesPage.vue"),       meta: { requiresAuth: true } },
  { path: "/app-tools/new",       name: "app-tool-new",       component: () => import("../pages/ExperienceEditPage.vue"),    meta: { requiresAuth: true } },
  { path: "/app-tools/:id/edit",  name: "app-tool-edit-id",   component: () => import("../pages/ExperienceEditPage.vue"),    props: true, meta: { requiresAuth: true } },
  // Трек 4 — каталог игр (game_definitions): витрина + управление своими играми тенанта.
  { path: "/games",               name: "games",              component: () => import("../pages/GamesPage.vue"),             meta: { requiresAuth: true, permission: "games:read" } },
  { path: "/suppressions",        name: "suppressions",     component: () => import("../pages/SuppressionPage.vue"),         meta: { requiresAuth: true, permission: "suppressions:read" } },
  // §12 — редактор внешнего вида мессенджера (presentation config) per-пространство/сегмент.
  { path: "/chat-presentation",   name: "chat-presentation", component: () => import("../pages/ChatPresentationConfigPage.vue"), meta: { requiresAuth: true, permission: "chat:update" } },
  // Ф1 productization — пространства мессенджера (N поверхностей per тенант, свой вид у каждой).
  { path: "/messenger/spaces",    name: "messenger-spaces", component: () => import("../pages/MessengerSpacesPage.vue"), meta: { requiresAuth: true, permission: "chat:update" } },
  // «Автоматизации отделов» (no-code «рабочие боты», Ф1): список + конструктор + «Подключения».
  // Статический /new ПЕРЕД /:id, чтобы «new» не парсился как id.
  { path: "/work-automations",     name: "work-automations",     component: () => import("../pages/WorkAutomationsPage.vue"),      meta: { requiresAuth: true, permission: "work_automations:list" } },
  { path: "/work-automations/new", name: "work-automation-new",  component: () => import("../pages/WorkAutomationEditorPage.vue"), meta: { requiresAuth: true, permission: "work_automations:read" } },
  { path: "/work-automations/:id", name: "work-automation-edit", component: () => import("../pages/WorkAutomationEditorPage.vue"), props: true, meta: { requiresAuth: true, permission: "work_automations:read" } },
  // /work-connections упразднён (единый реестр integration_connection) → редирект на единую страницу (закладки).
  { path: "/work-connections",     redirect: "/integration-connections" },
  { path: "/integration-connections", name: "integration-connections", component: () => import("../pages/IntegrationConnectionsPage.vue"), meta: { requiresAuth: true, permission: "bots:read" } },
  // ── Inv-2 Track #2 «Closed-loop журнеи»: опросы (NPS) + рефералы + инбокс оператору ──
  // Статический /new ПЕРЕД /:id, чтобы «new» не парсился как id.
  { path: "/surveys",             name: "surveys",          component: () => import("../pages/SurveysPage.vue"),             meta: { requiresAuth: true } },
  { path: "/surveys/new",         name: "survey-new",       component: () => import("../pages/SurveyEditPage.vue"),          meta: { requiresAuth: true } },
  // Канон навигации (как у журнеев, ADR-universal-navigation): /:id = ОТЧЁТ
  // (деталь), /:id/edit = редактор. Клик по опросу в списке открывает отчёт.
  { path: "/surveys/:id",         name: "survey-detail",    component: () => import("../pages/SurveyDashboardPage.vue"),     props: true, meta: { requiresAuth: true } },
  { path: "/surveys/:id/edit",    name: "survey-edit",      component: () => import("../pages/SurveyEditPage.vue"),          props: true, meta: { requiresAuth: true } },
  // Бэк-совместимость: старая ссылка на дашборд → отчёт (= деталь).
  { path: "/surveys/:id/dashboard", redirect: (to) => ({ name: "survey-detail", params: { id: to.params.id } }) },
  // B16 — full-screen GrapesJS builder for the branded survey LANDING page.
  // `bare` (no AppShell) + lazy-loaded (GrapesJS is heavy), same as email-builder.
  { path: "/surveys/:id/landing",   name: "survey-landing",   component: () => import("../pages/SurveyLandingBuilderPage.vue"), props: true, meta: { requiresAuth: true, bare: true } },
  { path: "/referrals",           name: "referrals",        component: () => import("../pages/ReferralsPage.vue"),           meta: { requiresAuth: true } },
  { path: "/referrals/new",       name: "referral-new",     component: () => import("../pages/ReferralProgramEditPage.vue"), meta: { requiresAuth: true } },
  // Канон (ADR-universal-navigation): /referrals/:id = ДЕТАЛЬ, /referrals/:id/edit = редактор.
  { path: "/referrals/:id/edit",  name: "referral-edit",    component: () => import("../pages/ReferralProgramEditPage.vue"), props: true, meta: { requiresAuth: true } },
  { path: "/referrals/:id",       name: "referral-detail",  component: () => import("../pages/ReferralProgramDetailPage.vue"), props: true, meta: { requiresAuth: true } },
  { path: "/operator-tasks",      name: "operator-tasks",   component: () => import("../pages/OperatorTasksPage.vue"),       meta: { requiresAuth: true } },
  // Wave 3 — added 2026-05-27.
  { path: "/scenarios-oob",       name: "scenarios-oob",    component: () => import("../pages/ScenariosPage.vue"),          meta: { requiresAuth: true } },
  // F23 — declare meta.permission on sensitive admin/governance/data routes so a
  // role lacking the permission can't even navigate to a page the backend would
  // 403 anyway (keeps forbidden screens out of the UI). Keys verified ∈ rbac.ts.
  { path: "/admin/users",         name: "admin-users",      component: () => import("../pages/AdminUsersPage.vue"),         meta: { requiresAuth: true, permission: "users:list" } },
  { path: "/admin/roles",         name: "admin-roles",      component: () => import("../pages/RolesAccessPage.vue"),        meta: { requiresAuth: true, permission: "roles:list" } },
  { path: "/admin/approvals",     name: "admin-approvals",  component: () => import("../pages/MakerCheckerPage.vue"),       meta: { requiresAuth: true, permission: "approvals:list" } },
  { path: "/admin/tenants",       name: "admin-tenants",    component: () => import("../pages/AdminTenantsPage.vue"),       meta: { requiresAuth: true } },
  // Keycloak/OIDC SSO per-tenant federation config (super_admin only).
  { path: "/admin/sso",           name: "admin-sso",        component: () => import("../pages/AuthConfigPage.vue"),         meta: { requiresAuth: true, permission: "auth_config:read" } },
  { path: "/admin/ai-costs",      name: "admin-ai-costs",   component: () => import("../pages/AiCostPage.vue"),             meta: { requiresAuth: true, permission: "ai_costs:read" } },
  // Ф2 / S17 — DSAR register (152-ФЗ): journal of personal-data exports/erasures.
  { path: "/admin/dsar",          name: "admin-dsar",       component: () => import("../pages/DsarRegisterPage.vue"),       meta: { requiresAuth: true, permission: "dsar:read" } },
  { path: "/settings/ai-pricing", name: "settings-ai-pricing", component: () => import("../pages/AiPricingSettingsPage.vue"), meta: { requiresAuth: true, permission: "ai_pricing:read" } },
  { path: "/settings/ai-providers", name: "settings-ai-providers", component: () => import("../pages/AiProvidersPage.vue"), meta: { requiresAuth: true, permission: "ai_pricing:read" } },
  { path: "/settings/ai-agents", name: "settings-ai-agents", component: () => import("../pages/AiAgentsPage.vue"), meta: { requiresAuth: true, permission: "bots:read" } },
  { path: "/settings/mcp-servers", name: "settings-mcp-servers", component: () => import("../pages/AgentMcpServersPage.vue"), meta: { requiresAuth: true, permission: "bots:read" } },
  { path: "/settings/bot-glossary", name: "settings-bot-glossary", component: () => import("../pages/BotGlossaryPage.vue"), meta: { requiresAuth: true, permission: "bots:read" } },
  { path: "/settings/bot-behavior", name: "settings-bot-behavior", component: () => import("../pages/BotBehaviorPage.vue"), meta: { requiresAuth: true, permission: "bots:read" } },
  { path: "/settings/code-library", name: "settings-code-library", component: () => import("../pages/CodeLibraryPage.vue"), meta: { requiresAuth: true, permission: "bots:read" } },
  { path: "/catalog-items",       name: "catalog-items",    component: () => import("../pages/CatalogItemsPage.vue"),       meta: { requiresAuth: true } },
  { path: "/dictionaries",        name: "dictionaries",     component: () => import("../pages/DictionariesPage.vue"),       meta: { requiresAuth: true } },
  { path: "/recommendations/test", name: "recommendations-test", component: () => import("../pages/RecommendationsTestPage.vue"), meta: { requiresAuth: true } },
  { path: "/ai-assistant",        name: "ai-assistant",     component: () => import("../pages/AiAssistantPage.vue"),        meta: { requiresAuth: true } },
  { path: "/wow-stats",           name: "wow-stats",        component: () => import("../pages/WowStatsPage.vue"),           meta: { requiresAuth: true } },
  // Wave 6 — Event ingestion. Track D (Sources / Recognisers / Enrichments /
  // Policy) ships real pages; Track E owns events log + replay (still on stub).
  {
    path: "/automations/sources",
    name: "automations-sources",
    component: () => import("../pages/automations/SourcesPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/automations/recognisers",
    name: "automations-recognisers",
    component: () => import("../pages/automations/RecognisersPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/automations/recognisers/new",
    name: "automations-recogniser-new",
    component: () => import("../pages/automations/RecogniserEditPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/automations/recognisers/:id/edit",
    name: "automations-recogniser-edit",
    component: () => import("../pages/automations/RecogniserEditPage.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: "/automations/enrichments",
    name: "automations-enrichments",
    component: () => import("../pages/automations/EnrichmentsPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/automations/enrichments/new",
    name: "automations-enrichment-new",
    component: () => import("../pages/automations/EnrichmentEditPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/automations/enrichments/:id/edit",
    name: "automations-enrichment-edit",
    component: () => import("../pages/automations/EnrichmentEditPage.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: "/automations/events",
    name: "automations-events",
    component: () => import("../pages/automations/EventsLogPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/automations/policy",
    name: "automations-policy",
    component: () => import("../pages/automations/PolicyPage.vue"),
    meta: { requiresAuth: true, permission: "ingestion_policies:read" },
  },
  {
    path: "/automations/replay",
    name: "automations-replay",
    component: () => import("../pages/automations/ReplayPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/settings",
    name: "settings",
    component: () => import("../pages/SettingsPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Public Preferences Center — no auth, bare layout (no AppShell).
    // The :token in the URL IS the auth. Backend rate-limits per IP.
    path: "/p/:token",
    name: "public-preferences",
    component: () => import("../pages/PublicPreferencesPage.vue"),
    meta: { bare: true },
  },
  {
    // Inv-2 — публичная страница ответа на опрос (без auth, bare). :token = auth.
    path: "/s/:token",
    name: "public-survey",
    component: () => import("../pages/PublicSurveyPage.vue"),
    meta: { bare: true },
  },
  {
    // Блок 1.2 — публичный лендинг ВЫБОРА КАНАЛА (без auth, bare). Скан QR визита
    // ведёт сюда: ответить в браузере (/s/<code>) или открыть бота в Telegram.
    path: "/q/:token",
    name: "public-survey-channel",
    component: () => import("../pages/PublicSurveyChannelPage.vue"),
    meta: { bare: true },
  },
  {
    // Inv-2 — публичная страница-приглашение реферала (без auth, bare). :code = ключ поиска.
    path: "/r/:code",
    name: "public-invite",
    component: () => import("../pages/PublicInvitePage.vue"),
    meta: { bare: true },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("../pages/NotFoundPage.vue"),
    meta: { bare: true },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from) => {
  const auth = useAuthStore();
  // Block the first navigation until bootstrap finishes. Without this, the
  // initial navigation triggered by `app.use(router)` runs BEFORE
  // `auth.bootstrap()` in main.ts has had a chance to set `user.value`, so
  // every page reload onto a `requiresAuth` route bounced through /login.
  // The check is gated on `bootstrapped` so subsequent navigations don't
  // re-fetch /v1/auth/me.
  if (!auth.bootstrapped) {
    await auth.bootstrap();
  }
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    const query: Record<string, string> = { redirect: to.fullPath };
    // If bootstrap detected a revoked session (had-session flag + 401),
    // surface the «Сессия истекла» banner on /login. Fresh visits to a
    // protected URL don't get the banner — that would be misleading.
    if (auth.sessionExpired) query.expired = "1";
    return { name: "login", query };
  }
  if (to.meta.publicOnly && auth.isAuthenticated) {
    return { name: "dashboard" };
  }
  // NON5 / R3 — forced password change. The server hard-blocks every route except
  // self/logout/change-password while must_change_password is set; mirror that
  // in the UI by funnelling the user to Settings (which hosts the
  // ChangePasswordCard banner + form) until the flag clears. R3: НЕ молчаливый
  // редирект (оператор видел «мёртвый» клик по меню без объяснения) — даём явный
  // toast «сначала смените пароль», и редиректим на форму смены. Toast показываем
  // только когда РЕАЛЬНО блокируем переход на другую страницу (не на самой /settings
  // и не при перезаходе на тот же путь) — иначе спамил бы на каждый guard-eval.
  const mustChange = mustChangePasswordDecision(to, from, auth);
  if (mustChange.redirect) {
    if (mustChange.toast) {
      useToast().push({ kind: "info", message: t("auth.must_change_redirect") });
    }
    return { path: "/settings" };
  }
  // Fine-grained permission gate (RBAC Block B6). A route may declare
  // `meta.permission` (a `resource:action` string); if the session lacks it,
  // bounce to the dashboard. The backend route guard is the real enforcement —
  // this just keeps a forbidden page out of the UI.
  if (to.meta.permission && auth.isAuthenticated && !auth.can(to.meta.permission as string)) {
    return { name: "dashboard" };
  }
  return true;
});
